import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useExchangeRates } from '@/hooks/useExchangeRates';

interface Locale {
  country_code: string;
  country_name: string;
  language_code: string;
  currency_code: string;
  currency_symbol: string;
}

interface LocaleContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  setLanguage: (languageCode: string) => void;
  supportedLocales: Locale[];
  isLoading: boolean;
  formatPrice: (priceInCents: number, originalCurrency?: string) => string;
  detectingLocation: boolean;
  exchangeRatesLoading: boolean;
  exchangeRatesLastUpdated: Date | null;
}

const defaultLocale: Locale = {
  country_code: 'US',
  country_name: 'United States',
  language_code: 'en',
  currency_code: 'USD',
  currency_symbol: '$',
};

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

export const LocaleProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const { rates, loading: exchangeRatesLoading, lastUpdated: exchangeRatesLastUpdated, convert } = useExchangeRates();
  const [locale, setLocaleState] = useState<Locale>(defaultLocale);
  const [supportedLocales, setSupportedLocales] = useState<Locale[]>([defaultLocale]);
  const [isLoading, setIsLoading] = useState(true);
  const [detectingLocation, setDetectingLocation] = useState(false);

  // Fetch supported locales
  useEffect(() => {
    const fetchLocales = async () => {
      try {
        // Use type assertion since table is new and not in generated types yet
        const { data, error } = await (supabase
          .from('supported_locales' as any)
          .select('country_code, country_name, language_code, currency_code, currency_symbol')
          .order('country_name') as any);

        if (error) throw error;
        if (data && data.length > 0) {
          setSupportedLocales(data);
        }
      } catch (error) {
        console.error('Error fetching locales:', error);
        // Keep default locale if fetch fails
      }
    };

    fetchLocales();
  }, []);

  // Load saved locale or detect location
  useEffect(() => {
    const loadLocale = async () => {
      setIsLoading(true);

      // First check localStorage
      const savedLocale = localStorage.getItem('user_locale');
      if (savedLocale) {
        try {
          const parsed = JSON.parse(savedLocale);
          setLocaleState(parsed);
          setIsLoading(false);
          return;
        } catch {
          // Invalid saved locale, continue to detection
        }
      }

      // If user is logged in, check their profile preference
      if (user) {
        try {
          const { data: profile } = await supabase
            .from('profiles')
            .select('preferred_country_code')
            .eq('user_id', user.id)
            .single();

          if ((profile as any)?.preferred_country_code) {
            const matchedLocale = supportedLocales.find(
              (l) => l.country_code === (profile as any).preferred_country_code
            );
            if (matchedLocale) {
              setLocaleState(matchedLocale);
              localStorage.setItem('user_locale', JSON.stringify(matchedLocale));
              setIsLoading(false);
              return;
            }
          }
        } catch {
          // Continue to geolocation
        }
      }

      // Auto-detect location using timezone (fast, no network call)
      setDetectingLocation(true);
      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const countryCode = getCountryFromTimezone(timezone);

        if (countryCode) {
          const matchedLocale = supportedLocales.find(
            (l) => l.country_code === countryCode
          );
          if (matchedLocale) {
            setLocaleState(matchedLocale);
            localStorage.setItem('user_locale', JSON.stringify(matchedLocale));
          }
        }
      } catch {
        // Silently fall back to default locale
      } finally {
        setDetectingLocation(false);
        setIsLoading(false);
      }
    };

    // Helper function to map timezone to country code
    function getCountryFromTimezone(timezone: string): string | null {
      const timezoneToCountry: Record<string, string> = {
        // United States
        "America/New_York": "US",
        "America/Chicago": "US",
        "America/Denver": "US",
        "America/Los_Angeles": "US",
        "America/Phoenix": "US",
        "America/Anchorage": "US",
        "Pacific/Honolulu": "US",
        // United Kingdom
        "Europe/London": "GB",
        // Germany
        "Europe/Berlin": "DE",
        // France
        "Europe/Paris": "FR",
        // Spain
        "Europe/Madrid": "ES",
        // Italy
        "Europe/Rome": "IT",
        // Netherlands
        "Europe/Amsterdam": "NL",
        // Belgium
        "Europe/Brussels": "BE",
        // Austria
        "Europe/Vienna": "AT",
        // Switzerland
        "Europe/Zurich": "CH",
        // Australia
        "Australia/Sydney": "AU",
        "Australia/Melbourne": "AU",
        "Australia/Brisbane": "AU",
        "Australia/Perth": "AU",
        // Canada
        "America/Toronto": "CA",
        "America/Vancouver": "CA",
        "America/Montreal": "CA",
        // Ireland
        "Europe/Dublin": "IE",
        // Sweden
        "Europe/Stockholm": "SE",
        // Norway
        "Europe/Oslo": "NO",
        // Denmark
        "Europe/Copenhagen": "DK",
        // Finland
        "Europe/Helsinki": "FI",
        // Poland
        "Europe/Warsaw": "PL",
        // Portugal
        "Europe/Lisbon": "PT",
        // Czech Republic
        "Europe/Prague": "CZ",
        // Greece
        "Europe/Athens": "GR",
        // Hungary
        "Europe/Budapest": "HU",
        // Romania
        "Europe/Bucharest": "RO",
        // Japan
        "Asia/Tokyo": "JP",
        // Singapore
        "Asia/Singapore": "SG",
        // Hong Kong
        "Asia/Hong_Kong": "HK",
        // India
        "Asia/Kolkata": "IN",
        // New Zealand
        "Pacific/Auckland": "NZ",
        // South Africa
        "Africa/Johannesburg": "ZA",
        // Brazil
        "America/Sao_Paulo": "BR",
        // Mexico
        "America/Mexico_City": "MX",
        // Argentina
        "America/Argentina/Buenos_Aires": "AR",
        // EU (map to closest match)
        "Europe/Luxembourg": "EU",
      };
      return timezoneToCountry[timezone] || null;
    }

    if (supportedLocales.length > 0) {
      loadLocale();
    }
  }, [user, supportedLocales]);

  const setLocale = async (newLocale: Locale) => {
    setLocaleState(newLocale);
    localStorage.setItem('user_locale', JSON.stringify(newLocale));

    // Save to user profile if logged in
    if (user) {
      try {
        await supabase
          .from('profiles')
          .update({ preferred_country_code: newLocale.country_code } as any)
          .eq('user_id', user.id);
      } catch (error) {
        console.error('Error saving locale preference:', error);
      }
    }
  };

  const setLanguage = async (languageCode: string) => {
    const updatedLocale = { ...locale, language_code: languageCode };
    setLocaleState(updatedLocale);
    localStorage.setItem('user_locale', JSON.stringify(updatedLocale));

    // Save to user profile if logged in
    if (user) {
      try {
        await supabase
          .from('profiles')
          .update({ preferred_language: languageCode } as any)
          .eq('user_id', user.id);
      } catch (error) {
        console.error('Error saving language preference:', error);
      }
    }
  };

  const formatPrice = (priceInCents: number, originalCurrency = 'USD'): string => {
    const priceInDollars = priceInCents / 100;
    
    // Use live exchange rates
    const convertedPrice = convert(priceInDollars, originalCurrency, locale.currency_code);

    // Format based on currency
    try {
      const formatter = new Intl.NumberFormat(
        locale.language_code + '-' + locale.country_code,
        {
          style: 'currency',
          currency: locale.currency_code,
          minimumFractionDigits: locale.currency_code === 'JPY' ? 0 : 2,
          maximumFractionDigits: locale.currency_code === 'JPY' ? 0 : 2,
        }
      );
      return formatter.format(convertedPrice);
    } catch {
      // Fallback formatting
      return `${locale.currency_symbol}${convertedPrice.toFixed(2)}`;
    }
  };

  return (
    <LocaleContext.Provider
      value={{
        locale,
        setLocale,
        setLanguage,
        supportedLocales,
        isLoading,
        formatPrice,
        detectingLocation,
        exchangeRatesLoading,
        exchangeRatesLastUpdated,
      }}
    >
      {children}
    </LocaleContext.Provider>
  );
};

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (context === undefined) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};
